import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Brain, Zap, Gauge, Crown } from "lucide-react";

import { PlanType, SubscriptionStatus, CODEX_MODELS, getAgentEngine, resolveSelectableModel } from "@optidev/shared-types";
import { usePlanFeatures } from "@/hooks/useFeatures";
import { useCodexEnabled } from "@/hooks/use-codex-enabled";
import { billingApi } from "@/lib/api/billing";
import { auth } from "@/lib/firebase";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ModelType is a string to support both Claude model keys and OpenRouter model IDs
export type ModelType = string;

interface ModelOption {
  label: string;
  modelName: string;
  planTier: string; // maps to planFeatures.models entries: 'haiku' | 'sonnet' | 'opus'
  descriptor: string; // short label shown at row end (Premium, Default, Fast...)
  provider: string; // e.g. "Anthropic", "OpenAI"
  tagline: string; // one-line "when to pick this" hint
  description: string; // longer hover description
  bestFor: string[]; // chips in hover panel
  intelligence: 1 | 2 | 3 | 4 | 5; // capability tier
  speed: 1 | 2 | 3 | 4 | 5; // response speed tier
  costTier: 1 | 2 | 3 | 4 | 5; // 1 = cheapest, 5 = most expensive
}

const claudeModels: Record<string, ModelOption> = {
  "claude-4-opus": {
    label: "Claude Opus 5.5",
    modelName: "Claude Opus 5.5",
    planTier: "opus",
    descriptor: "Most capable",
    provider: "Anthropic",
    tagline: "Smartest model — for complex refactors and tricky bugs",
    description:
      "Most capable Claude. Pick for multi-file refactors, deep reasoning, and agentic work where you need it right the first time.",
    bestFor: ["Complex refactors", "Multi-step agents", "Hard bugs"],
    intelligence: 5,
    speed: 2,
    costTier: 5,
  },
  // Key stays "claude-4-sonnet" (the stored workspace-default value); it now
  // resolves to the latest Sonnet (claude-sonnet-5-5) via app-config getClaudeModel.
  "claude-4-sonnet": {
    label: "Claude Sonnet 5.5",
    modelName: "Claude Sonnet 5.5",
    planTier: "sonnet",
    descriptor: "Balanced",
    provider: "Anthropic",
    tagline: "Best all-rounder — near-Opus intelligence at Sonnet price",
    description:
      "Anthropic's latest Sonnet. Near-Opus reasoning with strong agentic coding and tool use, at a lower price. The best default for most work.",
    bestFor: ["Coding", "Agentic builds", "Refactors", "Debugging"],
    intelligence: 5,
    speed: 4,
    costTier: 3,
  },
  "claude-4-haiku": {
    label: "Claude 4.5 Haiku",
    modelName: "Claude 4.5 Haiku",
    planTier: "haiku",
    descriptor: "Fastest Claude",
    provider: "Anthropic",
    tagline: "Quick edits and small changes when speed beats depth",
    description:
      "Fastest, cheapest Claude. Great for renames, small edits, and tight iteration where deep reasoning isn't needed.",
    bestFor: ["Quick edits", "Renames", "Copy tweaks"],
    intelligence: 3,
    speed: 5,
    costTier: 2,
  },
};

// OpenRouter models (no Claude - those use Claude engine directly)
// Model keys use OpenRouter model IDs (with "/" = OpenCode engine)
const openRouterModels: Record<string, ModelOption> = {
  // GPT-5.6 Terra retired (2026-10-30): OpenAI models run on the Codex engine now. Persisted
  // selections are remapped to codex/gpt-6.1-sol by sanitizeModel() (RETIRED_MODEL_REMAP).
  // Qwen 3.7 Plus dropped (2026-10-05): OpenRouter's only provider (Alibaba) has no
  // zero-data-retention endpoint, and the org now requires ZDR. sanitizeModel() moves
  // anyone still set to it onto the default.
  "xiaomi/mimo-v2.6-pro": {
    label: "MiMo V2.6 Pro",
    modelName: "MiMo V2.6 Pro",
    planTier: "sonnet",
    descriptor: "Best value",
    provider: "Xiaomi",
    tagline: "Near-frontier agentic coding at a budget price — slower replies",
    description:
      "Xiaomi's flagship. Strong agentic coding and tool use at a fraction of frontier pricing, and it can read screenshots. Replies start slower than most.",
    bestFor: ["Coding", "Agentic builds", "Budget runs"],
    intelligence: 5,
    speed: 3,
    costTier: 1,
  },
  // TEMPORARILY HIDDEN (2026-06): OpenCode↔Gemini serializer breaks on long
  // agentic tool-loops (OpenRouter 400 invalid_request / "Provider returned error").
  // To re-enable, just uncomment this block. Any workspace/user still set to this
  // model is auto-migrated to the default by sanitizeModel() (unavailable → default).
  // "google/gemini-3.5-flash": {
  //   label: "Gemini 3.5 Flash",
  //   modelName: "Gemini 3.5 Flash",
  //   planTier: "sonnet",
  //   descriptor: "Near-Pro, efficient",
  //   provider: "Google",
  //   tagline: "Near-Pro coding at Flash speed and cost",
  //   description:
  //     "Google's high-efficiency model. Near-Pro coding and reasoning at Flash-tier cost and speed. Strong multimodal (image, video, audio, PDF) and tuned for agentic loops.",
  //   bestFor: ["Coding", "Multimodal", "Agentic loops"],
  //   intelligence: 4,
  //   speed: 5,
  //   costTier: 2,
  // },
};

// Codex engine models (`codex/` prefix → OpenAI models through the Codex harness, #90).
// Catalog lives in shared-types so api + web + sidecar agree on keys and tiers.
const codexModels: Record<string, ModelOption> = Object.fromEntries(
  Object.entries(CODEX_MODELS).map(([key, def]) => [
    key,
    {
      label: def.label,
      modelName: def.label,
      planTier: def.planTier,
      descriptor: def.descriptor,
      provider: "OpenAI · Codex",
      tagline: def.tagline,
      description: def.description,
      bestFor: def.bestFor,
      intelligence: def.intelligence,
      speed: def.speed,
      costTier: def.costTier,
    } satisfies ModelOption,
  ]),
);

// All models combined - engine determined by model key format (codex/ = Codex, other "/" = OpenRouter)
const allModels: Record<string, ModelOption> = {
  ...claudeModels,
  ...openRouterModels,
  ...codexModels,
};

/**
 * Get all available models. Codex models are included only when the engine is enabled for
 * the viewer (feature flag or internal user) — pass `includeCodex: false` otherwise so a
 * persisted `codex/…` selection cannot survive on a flag-off environment.
 */
export function getModelConfig(opts?: { includeCodex?: boolean }): Record<string, ModelOption> {
  if (opts?.includeCodex === false) {
    return { ...claudeModels, ...openRouterModels };
  }
  return allModels;
}

/** Codex model keys (for grouping in pickers). */
export function isCodexModelKey(key: string): boolean {
  return key in codexModels;
}

/** Get the default model key */
export function getDefaultModel(): ModelType {
  return "claude-4-sonnet";
}

/**
 * Returns the model if it's a currently-available option, otherwise the default.
 * Use to migrate any persisted/selected value off a hidden or unknown model
 * (e.g. the temporarily-hidden Gemini) before it reaches the agent. Generic on
 * purpose: re-adding a model to the list above makes it pass through again.
 */
export function sanitizeModel(
  model?: string | null,
  opts?: { codexEnabled?: boolean },
): ModelType {
  const codexEnabled = opts?.codexEnabled !== false;
  const config = getModelConfig({ includeCodex: codexEnabled });
  // Retired keys (GPT-5.6 Terra → Codex Sol) and flag-off Codex keys resolve via the shared
  // helper so web, workspace default and gateway agree on the replacement.
  const resolved = resolveSelectableModel(model, { codexEnabled, fallback: getDefaultModel() });
  return config[resolved] ? resolved : getDefaultModel();
}

/** Determine engine from model key (shared contract with api + sidecar). */
export function getEngineFromModel(model: string): "claude" | "opencode" | "codex" {
  return getAgentEngine(model);
}

interface MeterProps {
  value: number;
  max?: number;
  className?: string;
}

/** Five-dot horizontal meter. */
function Meter({ value, max = 5, className }: MeterProps): React.ReactElement {
  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "h-1.5 w-3 rounded-sm",
            i < value ? "bg-foreground/70" : "bg-foreground/15",
          )}
        />
      ))}
    </div>
  );
}

/** Cost tier shown as $ symbols (1 = cheapest, 5 = priciest). */
function CostTier({ tier }: { tier: number }): React.ReactElement {
  return (
    <span className="font-mono text-[11px] tracking-tight">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={cn(
            i < tier ? "text-foreground/80" : "text-foreground/20",
          )}
        >
          $
        </span>
      ))}
    </span>
  );
}

function costLabel(tier: number): string {
  if (tier <= 1) return "Very low";
  if (tier === 2) return "Low";
  if (tier === 3) return "Medium";
  if (tier === 4) return "High";
  return "Very high";
}

interface ModelDropdownProps {
  model: ModelType;
  onChange: (model: ModelType) => void;
  disabled?: boolean;
  /** Collapse to an icon-only button (no text label) when space is tight */
  compact?: boolean;
}

export function ModelDropdown({
  model,
  onChange,
  disabled = false,
  compact = false,
}: ModelDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    bottom: number;
  } | null>(null);
  const navigate = useNavigate();
  const { planFeatures } = usePlanFeatures();
  const { enabled: codexEnabled } = useCodexEnabled();
  const [planType, setPlanType] = useState<PlanType>(PlanType.FREE);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);

  const models = getModelConfig({ includeCodex: codexEnabled });
  const currentEngine = getEngineFromModel(model || "claude-4-sonnet");
  console.debug(
    "[ModelDropdown] currentModel=%s, engine=%s, availableModels=%s",
    model,
    currentEngine,
    Object.keys(models).join(", "),
  );

  // Fetch user's subscription plan
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const token = await auth.getIdToken();
        if (token) {
          const subscription = await billingApi.getSubscription(token);
          setPlanType(subscription.planType);
          setSubscriptionStatus(subscription.status);
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
        setPlanType(PlanType.FREE);
        setSubscriptionStatus(null);
      }
    };
    fetchPlan();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      // Menu is portaled to body, so check it separately from the trigger
      if (
        !dropdownRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Derive the effective plan key. For embed workspaces, split into
  // `embed_trial` (status=trialing, no opus) vs `embed_active` (status=active,
  // opus unlocked). All other plans use planType directly.
  const effectivePlan: string =
    planType === PlanType.EMBED
      ? subscriptionStatus === SubscriptionStatus.TRIALING
        ? "embed_trial"
        : "embed_active"
      : planType;

  // Position the portaled menu above the trigger; it opens upward (bottom-anchored)
  const MENU_WIDTH = 300;
  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const left = Math.max(
        8,
        Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8),
      );
      setMenuPos({ left, bottom: window.innerHeight - rect.top + 6 });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  // Check if a model is allowed for the current plan
  const isModelAllowed = (modelKey: string): boolean => {
    const lookup = planFeatures as unknown as Record<
      string,
      { models: string[] } | undefined
    >;
    const currentPlanFeatures = lookup[effectivePlan] || planFeatures.free;
    const tier = models[modelKey]?.planTier;
    if (!tier) return true; // unknown model = allow
    return currentPlanFeatures.models.includes(tier);
  };

  const handleSelect = (newModel: ModelType) => {
    if (!isModelAllowed(newModel)) {
      return;
    }
    const newEngine = newModel.includes("/") ? "opencode" : "claude";
    const oldEngine = model?.includes("/") ? "opencode" : "claude";
    console.log(
      "[ModelDropdown] Selected: %s → %s (engine: %s → %s)",
      model,
      newModel,
      oldEngine,
      newEngine,
    );
    onChange(newModel);
    setIsOpen(false);
  };

  const currentConfig = models[model];
  const displayLabel = currentConfig?.modelName || "Claude Sonnet 5.5";

  // Group models for visual hierarchy in the dropdown
  const claudeEntries = Object.entries(models).filter(
    ([key]) => key in claudeModels,
  );
  const codexEntries = Object.entries(models).filter(
    ([key]) => key in codexModels,
  );
  const otherEntries = Object.entries(models).filter(
    ([key]) => !(key in claudeModels) && !(key in codexModels),
  );

  const renderRow = (key: string, config: ModelOption) => {
    const isSelected = model === key;
    const canUse = isModelAllowed(key);
    const isDefault = key === getDefaultModel();
    const isPremium = config.planTier === "opus";

    return (
      <Tooltip key={key} delayDuration={200} disableHoverableContent={false}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => canUse && handleSelect(key)}
            disabled={!canUse}
            className={cn(
              "w-full text-left px-2.5 py-1.5 rounded-md transition-colors duration-150",
              "flex items-center justify-between gap-2",
              !canUse
                ? "opacity-60 cursor-not-allowed"
                : isSelected
                  ? "bg-primary/10 dark:bg-primary/20 ring-1 ring-inset ring-primary/40 dark:ring-primary/60"
                  : "hover:bg-foreground/[0.04] dark:hover:bg-foreground/[0.06] cursor-pointer",
            )}
          >
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {isPremium && (
                <Crown
                  className="h-3.5 w-3.5 shrink-0 text-purple-500 dark:text-purple-300"
                  aria-label="Premium model"
                />
              )}
              <span
                className={cn(
                  "text-sm font-medium truncate",
                  isSelected
                    ? "text-primary dark:text-primary-foreground"
                    : "text-foreground",
                )}
              >
                {config.modelName.replace(" (Default)", "")}
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span
                className="inline-flex items-center gap-1 text-muted-foreground"
                title="Intelligence"
              >
                <Brain className="h-3 w-3" />
                <Meter value={config.intelligence} />
              </span>
              <CostTier tier={config.costTier} />
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          sideOffset={8}
          collisionPadding={16}
          className="max-w-[260px] bg-popover text-popover-foreground border border-border shadow-lg p-3 space-y-2"
        >
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-foreground">
                {config.modelName.replace(" (Default)", "")}
              </span>
              {isPremium && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/15 text-purple-500 dark:text-purple-300">
                  Premium
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {config.provider}
            </p>
          </div>

          <p className="text-[11px] leading-relaxed text-foreground/90">
            {config.description}
          </p>

          <div className="flex flex-wrap gap-1">
            {config.bestFor.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-foreground/5 text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="pt-1 border-t border-border space-y-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Brain className="h-3 w-3" /> Intelligence
              </span>
              <Meter value={config.intelligence} />
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Zap className="h-3 w-3" /> Speed
              </span>
              <Meter value={config.speed} />
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Gauge className="h-3 w-3" /> Cost
              </span>
              <span className="flex items-center gap-1.5">
                <CostTier tier={config.costTier} />
                <span className="text-muted-foreground">
                  {costLabel(config.costTier)}
                </span>
              </span>
            </div>
          </div>

          {!canUse && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
                navigate("/dashboard/settings/billing");
              }}
              className="mt-1 w-full inline-flex items-center justify-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-purple-500 text-white hover:bg-purple-600 transition-colors"
            >
              Upgrade plan to use
            </button>
          )}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={250}>
      <div className="relative" ref={dropdownRef}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          title={compact ? displayLabel.replace(" (Default)", "") : undefined}
          className={cn(
            "inline-flex items-center gap-1.5 h-7 rounded-md",
            compact ? "px-1.5" : "pl-2.5 pr-1.5",
            "border border-border bg-background dark:bg-muted/40 hover:bg-muted/80 hover:border-border",
            "text-foreground",
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            "transition-colors duration-150 text-xs font-medium",
            isOpen && "bg-muted/80",
          )}
        >
          {compact ? (
            <Brain className="w-3.5 h-3.5" />
          ) : (
            <span className="truncate max-w-[160px]">
              {displayLabel.replace(" (Default)", "")}
            </span>
          )}
          <ChevronDown
            className={cn(
              "w-3 h-3 text-muted-foreground transition-transform",
              isOpen && "rotate-180",
            )}
          />
        </button>

        {isOpen &&
          menuPos &&
          createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                left: menuPos.left,
                bottom: menuPos.bottom,
                width: MENU_WIDTH,
              }}
              className="bg-popover rounded-lg shadow-lg border border-border z-[9999] max-h-[480px] overflow-y-auto"
            >
              <div className="px-1.5 pt-1.5 pb-0.5">
              <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                Anthropic
              </div>
              <div className="flex flex-col gap-1">
                {claudeEntries.map(([key, config]) => renderRow(key, config))}
              </div>
            </div>

            {codexEntries.length > 0 && (
              <div className="px-1.5 pb-0.5">
                <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                  OpenAI · Codex
                </div>
                <div className="flex flex-col gap-1">
                  {codexEntries.map(([key, config]) => renderRow(key, config))}
                </div>
              </div>
            )}

            {otherEntries.length > 0 && (
              <div className="px-1.5 pb-1.5">
                <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                  Other providers
                </div>
                <div className="flex flex-col gap-1">
                  {otherEntries.map(([key, config]) => renderRow(key, config))}
                </div>
              </div>
            )}
            </div>,
            document.body,
          )}
      </div>
    </TooltipProvider>
  );
}
